import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Modal, Button } from "../../../../UI";
import { axios } from "../../../../lib";
import { getBikes } from "../../../../store/actions";
import Container from "./styles";

const RiderDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  
  const [activeTab, setActiveTab] = useState("details");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const rider = state || {};
  
  const [formData, setFormData] = useState({
    firstName: rider.firstName || '',
    lastName: rider.lastName || '',
    phone: rider.phone || '',
    vehicleType: rider.vehicleType || '',
    vehicleNumber: rider.vehicleNumber || '',
    licenseNumber: rider.licenseNumber || ''
  });

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`/rider/${rider.id}`, formData);
      
      if (response.status === 200) {
        toast.success("Rider updated successfully");
        setIsEditing(false);
        dispatch(getBikes({}));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update rider");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this rider? This action cannot be undone.")) {
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await axios.delete(`/rider/${rider.assignmentId}`);
      
      if (response.status === 200) {
        toast.success("Rider deleted successfully");
        navigate(-1);
        dispatch(getBikes({}));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete rider");
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.patch(`/rider/${rider.assignmentId}/status`);
      
      if (response.status === 200) {
        toast.success("Rider status updated successfully");
        dispatch(getBikes({}));
        navigate(-1);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Modal showModal={true}>
        <div className="riderDetails--container">
          <header>
            <div className="header-content">
              <div>
                <h2>Rider Details</h2>
                <p>{rider.firstName} {rider.lastName}</p>
              </div>
              <div className="header-actions">
                {!isEditing ? (
                  <>
                    <Button 
                      className="btn-edit"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                    <Button 
                      className="btn-delete"
                      onClick={handleDelete}
                      loading={deleteLoading}
                    >
                      Delete
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      className="btn-cancel"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          firstName: rider.firstName || '',
                          lastName: rider.lastName || '',
                          phone: rider.phone || '',
                          vehicleType: rider.vehicleType || '',
                          vehicleNumber: rider.vehicleNumber || '',
                          licenseNumber: rider.licenseNumber || ''
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="btn-save"
                      onClick={handleUpdate}
                      loading={loading}
                    >
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>
          </header>

          <div className="tabs">
            <button 
              className={activeTab === "details" ? "active" : ""}
              onClick={() => setActiveTab("details")}
            >
              Personal Details
            </button>
            <button 
              className={activeTab === "vehicle" ? "active" : ""}
              onClick={() => setActiveTab("vehicle")}
            >
              Vehicle Info
            </button>
            <button 
              className={activeTab === "assignment" ? "active" : ""}
              onClick={() => setActiveTab("assignment")}
            >
              Assignment
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "details" && (
              <div className="details-tab">
                <div className="detail-row">
                  <label>Rider ID</label>
                  <p>{rider.id || 'N/A'}</p>
                </div>

                <div className="detail-row">
                  <label>First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInput}
                      placeholder="Enter first name"
                    />
                  ) : (
                    <p>{rider.firstName || 'N/A'}</p>
                  )}
                </div>

                <div className="detail-row">
                  <label>Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInput}
                      placeholder="Enter last name"
                    />
                  ) : (
                    <p>{rider.lastName || 'N/A'}</p>
                  )}
                </div>

                <div className="detail-row">
                  <label>Email</label>
                  <p>{rider.email || 'N/A'}</p>
                </div>

                <div className="detail-row">
                  <label>Phone</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInput}
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <p>{rider.phone || 'N/A'}</p>
                  )}
                </div>

                <div className="detail-row">
                  <label>Created At</label>
                  <p>{rider.createdAt ? new Date(rider.createdAt).toLocaleString() : 'N/A'}</p>
                </div>
              </div>
            )}

            {activeTab === "vehicle" && (
              <div className="vehicle-tab">
                <div className="detail-row">
                  <label>Vehicle Type</label>
                  {isEditing ? (
                    <select
                      name="vehicleType"
                      value={formData.vehicleType}
                      onChange={handleInput}
                    >
                      <option value="">Select type</option>
                      <option value="MOTORCYCLE">Motorcycle</option>
                      <option value="BICYCLE">Bicycle</option>
                      <option value="CAR">Car</option>
                      <option value="VAN">Van</option>
                      <option value="TRUCK">Truck</option>
                    </select>
                  ) : (
                    <p>{rider.vehicleType || 'N/A'}</p>
                  )}
                </div>

                <div className="detail-row">
                  <label>Vehicle Number</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="vehicleNumber"
                      value={formData.vehicleNumber}
                      onChange={handleInput}
                      placeholder="Enter vehicle number"
                    />
                  ) : (
                    <p>{rider.vehicleNumber || 'N/A'}</p>
                  )}
                </div>

                <div className="detail-row">
                  <label>License Number</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleInput}
                      placeholder="Enter license number"
                    />
                  ) : (
                    <p>{rider.licenseNumber || 'N/A'}</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "assignment" && (
              <div className="assignment-tab">
                <div className="detail-row">
                  <label>Assignment ID</label>
                  <p>{rider.assignmentId || 'N/A'}</p>
                </div>

                <div className="detail-row">
                  <label>Company ID</label>
                  <p>{rider.companyId || 'Not Assigned'}</p>
                </div>

                <div className="detail-row">
                  <label>Assignment Status</label>
                  <p className={`status ${rider.assignmentStatus?.toLowerCase()}`}>
                    {rider.assignmentStatus || 'UNASSIGNED'}
                  </p>
                </div>

                {rider.assignmentId && (
                  <div className="detail-row">
                    <Button 
                      className="btn-toggle-status"
                      onClick={toggleStatus}
                      loading={loading}
                      full
                    >
                      Toggle Status
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="footer-actions">
            <Button 
              className="btn-close"
              onClick={() => navigate(-1)}
              full
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default RiderDetails;
